import React, { useState, useEffect } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from '../services/api';

const EditReviewModal = ({ isOpen, onClose, review, onReviewUpdated }) => {
    const [formData, setFormData] = useState({
        name: '',
        role: '',
        text: ''
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (review) {
            setFormData({
                name: review.name,
                role: review.role,
                text: review.text
            });
            setImagePreview(review.image);
            setImageFile(null); // Reset when review changes
        }
    }, [review]);

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            if (file.size > 5 * 1024 * 1024) {
                setError('Image size must be less than 5MB');
                return;
            }
            setImageFile(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    if (!isOpen || !review) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            let response;

            if (imageFile) {
                // If there's a new image, send as multipart/form-data
                const submitData = new FormData();
                submitData.append('name', formData.name);
                submitData.append('role', formData.role);
                submitData.append('text', formData.text);
                submitData.append('image', imageFile);

                response = await api.patch(`/reviews/${review._id}`, submitData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            } else {
                // If there's no new image, just send the text data as JSON
                response = await api.patch(`/reviews/${review._id}`, {
                    name: formData.name,
                    role: formData.role,
                    text: formData.text
                });
            }

            onReviewUpdated(response.data.data ? response.data.data : response.data);
            onClose();
        } catch (err) {
            console.error('Failed to update review:', err);
            setError(err.response?.data?.message || 'Failed to update review.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl w-full max-w-md p-6 relative animate-fade-in-up">
                <button
                    type="button"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 rounded-lg p-1 hover:bg-gray-100 transition-colors"
                >
                    <X className="w-5 h-5" />
                </button>

                <h3 className="text-2xl font-bold text-gray-900 mb-6">Edit Review</h3>

                {error && (
                    <div className="mb-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">
                        {error}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Image Upload Section */}
                    <div className="flex flex-col items-center gap-4 mb-4">
                        <div className="relative group w-24 h-24 rounded-full overflow-hidden border-2 border-dashed border-gray-300 hover:border-blue-500 transition-colors">
                            {imagePreview ? (
                                <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-full h-full object-cover"
                                />
                            ) : (
                                <div className="w-full h-full bg-gray-50 flex items-center justify-center text-gray-400">
                                    <span className="text-xs">No image</span>
                                </div>
                            )}
                            <label className="absolute inset-0 bg-black/50 hidden group-hover:flex items-center justify-center cursor-pointer transition-all">
                                <span className="text-white text-xs font-medium">Change</span>
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    className="hidden"
                                />
                            </label>
                        </div>
                        <p className="text-xs text-gray-500 text-center">
                            Click image to upload new portrait<br />
                            (Max 5MB)
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            value={formData.name}
                            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Role & Company</label>
                        <input
                            type="text"
                            required
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                            value={formData.role}
                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        />
                    </div>

                    <div>
                        <div className="flex justify-between items-center mb-1">
                            <label className="block text-sm font-medium text-gray-700">Review</label>
                            <span className={`text-xs ${formData.text.length >= 120 ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
                                {formData.text.length}/120
                            </span>
                        </div>
                        <textarea
                            required
                            maxLength={120}
                            className="w-full px-4 py-2 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 h-24 resize-none"
                            value={formData.text}
                            onChange={(e) => {
                                const val = e.target.value;
                                if (val.length <= 120) {
                                    setFormData({ ...formData, text: val });
                                }
                            }}
                        />
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {loading && <Loader2 className="w-5 h-5 animate-spin" />}
                        {loading ? 'Saving Changes...' : 'Save Changes'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default EditReviewModal;
